import { Edit } from '@mui/icons-material';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Avatar,
  CardContent,
  IconButton,
  TableCell as MuiTableCell,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { editReview } from '../../../api/products';
import useAuthToken from '../../../hooks/getUserData';
import Modal from '../../elements/Modal';
import ReviewForm from './ReviewForm';

const TableCell = ({ children, ...props }) => (
  <MuiTableCell align="center" {...props} sx={{ maxWidth: '160px' }}>
    {children}
  </MuiTableCell>
);

const EditReview = ({
  reviews,
  open,
  setOpen,
  productId,
  fetchProduct,
  setMessage,
  setSnackbarOpen,
}) => {
  const [error, setError] = useState(null);
  const [editableStates, setEditableStates] = useState(
    reviews.map(() => false)
  );
  const [visibilityStates, setVisibilityStates] = useState(
    reviews.map((review) => review.hide)
  );
  const token = useAuthToken();

  const handleClose = () => {
    setOpen(false);
    setError(null);
    setSnackbarOpen(true);
    fetchProduct();
  };

  const handleVisibilityToggle = async ({ index, userId }) => {
    const newVisibilityStates = [...visibilityStates];
    newVisibilityStates[index] = !newVisibilityStates[index];
    setVisibilityStates(newVisibilityStates);
    const payload = { hide: newVisibilityStates[index], userId: userId };
    await handleUpdate(payload);
  };

  const handleEditToggle = (index) => {
    const newEditableStates = [...editableStates];
    newEditableStates[index] = !newEditableStates[index];
    setEditableStates(newEditableStates);
  };

  const onSubmit = async (values) => {
    const payload = { updatedReview: values.review, userId: values.userId };
    handleUpdate(payload);
    handleEditToggle(values.index);
  };

  const handleUpdate = async (values) => {
    try {
      const response = await editReview(values, productId, token);
      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error?.message || errorResponse.message);
      }
      setMessage('Reviews updated successfully');
    } catch (error) {
      setError(error.message || 'An error occurred while updating review.');
    }
  };

  return (
    <Modal
      open={open}
      width={'lg'}
      modalTitle="Product reviews"
      handleClose={handleClose}>
      <CardContent sx={{ pt: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  'User',
                  'Type',
                  `User's review`,
                  'Updated review',
                  'Actions',
                ].map((heading, index) => (
                  <TableCell
                    key={index}
                    style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reviews.map((review, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: visibilityStates[index]
                      ? '#f0f0f0'
                      : 'inherit',
                  }}>
                  <TableCell
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textAlign: 'left',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={review.user.profile_pic}
                        alt={`${review.user.first_name}`}
                      />
                      <div style={{ marginLeft: '8px' }}>
                        <Typography>{`${review.user.first_name} ${review.user.last_name}`}</Typography>
                        <Typography variant="body2">
                          {review.user.email}
                        </Typography>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {review.type === 'buy' ? (
                      <ThumbUpIcon color="success" />
                    ) : review.type === 'skip' ? (
                      <ThumbDownIcon color="error" />
                    ) : null}
                  </TableCell>
                  <TableCell>{review.note}</TableCell>

                  <TableCell
                    sx={{
                      padding: 0,
                    }}>
                    <ReviewForm
                      review={review}
                      isEditable={editableStates[index]}
                      setEditable={() => handleEditToggle(index)}
                      onSubmit={(values) => onSubmit({ ...values, index })}
                    />
                  </TableCell>

                  <TableCell>
                    <IconButton
                      title={visibilityStates[index] ? 'Show' : 'Hide'}
                      onClick={() =>
                        handleVisibilityToggle({
                          index,
                          userId: review.user.id,
                        })
                      }>
                      {visibilityStates[index] ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>

                    <IconButton
                      title="Edit"
                      onClick={() => handleEditToggle(index)}
                      disabled={editableStates[index]}>
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Modal>
  );
};

export default EditReview;
